import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { SPRING, TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useDialogLayer } from '@/shared/hooks/useDialogLayer';

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Modal body — usually a `<div className="card">` with header + content + footer. */
  children: ReactNode;
  /**
   * If a click outside the modal body should close it. Default true.
   * Disable for flows that mustn't be dismissed by an accidental tap
   * (e.g. an OAuth callback waiting for the popup to return).
   */
  closeOnBackdropClick?: boolean;
  /** Optional aria-labelledby pointing at an id inside `children`. */
  labelledBy?: string;
  /** Optional aria-describedby pointing at an id inside `children`. */
  describedBy?: string;
  /** `alertdialog` for a confirmation that interrupts (ConfirmDialog). */
  role?: 'dialog' | 'alertdialog';
  /** z-index for the overlay. 600 by default — high enough to sit above
   *  app-shell modals like the Compose drawer (which uses z-150). */
  zIndex?: number;
}

/**
 * Centred dialog shell (C11's centred sibling of `Sheet`).
 *
 * Portalled to <body> so no transformed ancestor can trap the fixed overlay.
 * Every centred modal shares the same scrim as Sheet and CommandPalette
 * (`--scrim`, lavender ink at 24%, no blur), the panel timing for the scrim,
 * the SPRING token for the panel (rise only, no scale), focus trap/restore,
 * Escape and scroll lock. Reduced motion makes every step instant.
 */
export function ModalShell({
  open,
  onClose,
  children,
  closeOnBackdropClick = true,
  labelledBy,
  describedBy,
  role = 'dialog',
  zIndex = 600,
}: ModalShellProps) {
  const reduced = useReducedMotion() ?? false;

  // Escape (top layer only), focus in / trapped / restored, scroll lock.
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogLayer(open, onClose, panelRef);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
          transition={motionSafe(TRANSITION.panel, reduced)}
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex, padding: 16, background: 'var(--scrim)' }}
          onClick={closeOnBackdropClick ? onClose : undefined}
          role="presentation"
        >
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: 8 }}
            transition={motionSafe(SPRING, reduced)}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            role={role}
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            className="w-full max-w-full flex justify-center"
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
